import type { Zone } from '@seek/audience-zones';

import { getProfileSuggestionsExperimentVariant } from 'src/modules/features/profileSuggestionsAbTest';
import { getShareableProfileExperimentVariant } from 'src/modules/features/shareableProfileAbTest';
import { getVerificationsNudgeExperimentVariant } from 'src/modules/features/verificationsNudgeAbTest';

import type {
  AnalyticsProfileApproachabilityStatus,
  AnalyticsProfileSharedStatus,
  AnalyticsProfileVisibilityLevel,
  AnalyticsState,
} from '../types';

export interface ProfileState {
  candidateId?: string;
  profileVisibilityLevel?: AnalyticsProfileVisibilityLevel;
  profileApproachabilityStatus?: AnalyticsProfileApproachabilityStatus;
  profileSharedStatus?: AnalyticsProfileSharedStatus;
  profileViewedCount?: number;
}

const analyticsExperiments = ({
  zone,
  candidateId,
}: {
  zone?: Zone;
  candidateId?: string;
}) => {
  const experiments: AnalyticsState['experiments'] = [];

  if (zone && candidateId) {
    const shareablePorfileExperimentVariant =
      getShareableProfileExperimentVariant(zone, 'profile-page', candidateId);

    const verificationsNudgeExperimentVariant =
      getVerificationsNudgeExperimentVariant({
        zone,
        page: 'profile-page',
        candidateId,
      });

    const profileSuggestionsExperimentVariant =
      getProfileSuggestionsExperimentVariant({
        zone,
        page: 'profile-page',
        candidateId,
      });

    if (verificationsNudgeExperimentVariant) {
      experiments.push({
        num: 1,
        id: 'verify_work_email',
        variant: verificationsNudgeExperimentVariant,
      });
    }

    if (profileSuggestionsExperimentVariant) {
      experiments.push({
        num: 3,
        id: 'centralised_nudge',
        variant: profileSuggestionsExperimentVariant,
      });
    }

    if (shareablePorfileExperimentVariant) {
      experiments.push({
        num: 2,
        id: 'shareable_profile',
        variant: shareablePorfileExperimentVariant,
      });
    }

    if (experiments.length) {
      return experiments;
    }
  }
};

export const profileReducer = (
  state: AnalyticsState,
  props: ProfileState,
): AnalyticsState => {
  const {
    profileVisibilityLevel = state.profileVisibilityLevel,
    candidateId = state.loginId,
    profileApproachabilityStatus = state.profileApproachabilityStatus,
    profileSharedStatus = state.profileSharedStatus,
    profileViewedCount = state.profileViewedCount,
  } = props;

  return {
    ...state,
    profileVisibilityLevel: profileVisibilityLevel
      ? (profileVisibilityLevel?.toLowerCase() as AnalyticsProfileVisibilityLevel)
      : 'not set',
    profileApproachabilityStatus,
    profileSharedStatus,
    profileViewedCount,
    loginId: candidateId,
    experiments: analyticsExperiments({
      zone: state.zone,
      candidateId,
    }),
  };
};
